import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { syncService } from '../lib/supabase/services/syncService';
import { clearAllData, setCachedUserId } from '../lib/supabase/utils/localStorage';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(false);

  // Initialize user data from Supabase
  const initializeUserData = async (userId) => {
    if (!userId) return;

    setInitializing(true);
    try {
      console.log('[AuthContext] Initializing user data for:', userId);

      // 1. Check if migration from localStorage is needed
      const migrationResult = await syncService.migrateLocalStorageToSupabase(userId);

      if (migrationResult.success) {
        if (migrationResult.migrated) {
          console.log('[AuthContext] Migration completed successfully');
        } else if (migrationResult.alreadyMigrated) {
          console.log('[AuthContext] Already migrated');
        } else if (migrationResult.skipped) {
          console.log('[AuthContext] Migration skipped (data exists in Supabase)');
        }
      } else {
        console.warn('[AuthContext] Migration failed:', migrationResult.error);
        // Continue anyway - user can still use the app
      }

      // 2. Cache user ID
      setCachedUserId(userId);

      console.log('[AuthContext] User initialization complete');
    } catch (error) {
      console.error('[AuthContext] Error initializing user data:', error);
    } finally {
      setInitializing(false);
    }
  };

  useEffect(() => {
    // Check for demo mode first
    const isDemoMode = localStorage.getItem('demo_mode') === 'true';
    if (isDemoMode) {
      try {
        const demoUser = JSON.parse(localStorage.getItem('demo_user'));
        const demoSession = JSON.parse(localStorage.getItem('demo_session'));

        if (demoUser && demoSession) {
          setUser(demoUser);
          setSession(demoSession);
          setLoading(false);
          return;
        }
      } catch (error) {
        console.error('Error loading demo session:', error);
        // Clear invalid demo data
        localStorage.removeItem('demo_mode');
        localStorage.removeItem('demo_user');
        localStorage.removeItem('demo_session');
      }
    }

    // Get initial session from Supabase
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      // Initialize user data if logged in
      if (session?.user) {
        initializeUserData(session.user.id);
      }

      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Skip if in demo mode
      if (localStorage.getItem('demo_mode') === 'true') {
        return;
      }

      setSession(session);
      setUser(session?.user ?? null);

      // Handle different auth events
      if (event === 'SIGNED_IN' && session?.user) {
        // User just signed in - initialize data
        await initializeUserData(session.user.id);
      } else if (event === 'SIGNED_OUT') {
        // User signed out - clear local data
        clearAllData();
      }

      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signInWithMagicLink = async (email) => {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });
      
      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error signing in:', error);
      return { success: false, error: error.message };
    }
  };

  const signOut = async () => {
    try {
      // Check if demo mode
      const isDemoMode = localStorage.getItem('demo_mode') === 'true';
      if (isDemoMode) {
        // Clear demo mode
        localStorage.removeItem('demo_mode');
        localStorage.removeItem('demo_user');
        localStorage.removeItem('demo_session');
        setUser(null);
        setSession(null);
        return;
      }

      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const signInDemo = async () => {
    try {
      // Create a demo user object
      const demoUser = {
        id: 'demo-user-' + Date.now(),
        email: 'demo@efficacitas.local',
        user_metadata: {
          name: 'Demo User'
        },
        app_metadata: {},
        aud: 'authenticated',
        created_at: new Date().toISOString()
      };

      // Create a demo session
      const demoSession = {
        access_token: 'demo-token',
        refresh_token: 'demo-refresh',
        expires_in: 3600,
        token_type: 'bearer',
        user: demoUser
      };

      // Save demo mode flag to localStorage
      localStorage.setItem('demo_mode', 'true');
      localStorage.setItem('demo_user', JSON.stringify(demoUser));
      localStorage.setItem('demo_session', JSON.stringify(demoSession));

      // In production, clear any existing task/project/tag data
      // so the demo starts fresh every time
      if (!import.meta.env.DEV) {
        localStorage.removeItem('tasks');
        localStorage.removeItem('projects');
        localStorage.removeItem('tags');
      }

      // Set user and session state
      setUser(demoUser);
      setSession(demoSession);

      return { success: true };
    } catch (error) {
      console.error('Error signing in demo mode:', error);
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    session,
    loading,
    initializing,
    signInWithMagicLink,
    signInDemo,
    signOut
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};